import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { useCentralStore } from "@/dentlab/src/store/Central";
import {
	getSetupStatus,
	SetupStepStatus,
} from "@/dentlab/src/store/Central/selectors";
import { cn } from "@/dentlab/src/lib/utils/cn";

const SetupStepItem: React.FC<{ step: SetupStepStatus }> = ({ step }) => (
	<div
		className={cn(
			"flex items-center gap-3 py-2 px-3 rounded-md",
			step.isComplete ? "bg-emerald-50/50" : "bg-slate-50"
		)}
	>
		<div
			className={cn(
				"flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center",
				step.isComplete ? "bg-emerald-500" : "border-2 border-slate-300"
			)}
		>
			{step.isComplete && (
				<Check className="w-3 h-3 text-white" strokeWidth={3} />
			)}
		</div>
		<div className="flex-1 min-w-0">
			<div
				className={cn(
					"text-sm",
					step.isComplete ? "text-emerald-700" : "text-slate-600"
				)}
			>
				{step.label}
			</div>
			{!step.isComplete && step.missingFields.length > 0 && (
				<div className="text-xs text-slate-400 mt-0.5">
					{step.missingFields.join(", ")}
				</div>
			)}
		</div>
	</div>
);

export const EinrichtungSection: React.FC = () => {
	const navigate = useNavigate();

	const {
		organization,
		profile,
		activatedRegions,
		bankAccountsLookup,
		priceLists,
	} = useCentralStore((state) => ({
		organization: state.organization,
		profile: state.profile,
		activatedRegions: state.activatedRegions,
		bankAccountsLookup: state.bankAccountsLookup,
		priceLists: state.priceLists,
	}));

	const setupStatus = getSetupStatus(
		organization,
		profile,
		activatedRegions,
		bankAccountsLookup,
		priceLists
	);

	const completedCount = setupStatus.steps.filter((s) => s.isComplete).length;
	const totalCount = setupStatus.steps.length;
	const allComplete = completedCount === totalCount;

	const [isOpen, setIsOpen] = useState(!allComplete);

	return (
		<Collapsible.Root
			open={isOpen}
			onOpenChange={setIsOpen}
			className="bg-white rounded-lg mb-4"
		>
			<Collapsible.Trigger
				className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
				data-testid="quickstart-section-einrichtung"
				aria-label="Einrichtung"
			>
				<div className="text-left">
					<div className="flex items-center gap-2">
						{allComplete && (
							<CheckCircle2 className="w-5 h-5 text-emerald-600" />
						)}
						<h3
							className={cn(
								"text-lg font-semibold",
								allComplete
									? "text-emerald-700"
									: "text-gray-900"
							)}
						>
							Einrichtung
						</h3>
					</div>
					<p
						className={cn(
							"text-sm",
							allComplete ? "text-emerald-600" : "text-gray-600"
						)}
					>
						{completedCount} von {totalCount} abgeschlossen
					</p>
				</div>
				<div className="flex-shrink-0">
					{isOpen ? (
						<ChevronDown className="w-5 h-5 text-gray-400" />
					) : (
						<ChevronRight className="w-5 h-5 text-gray-400" />
					)}
				</div>
			</Collapsible.Trigger>
			<Collapsible.Content>
				<div className="border-t border-gray-200 px-4 py-3">
					<div className="flex flex-col gap-1.5">
						{setupStatus.steps.map((step) => (
							<SetupStepItem key={step.stepKey} step={step} />
						))}
					</div>
					{!allComplete && (
						<div className="flex gap-3 mt-4">
							<button
								onClick={() => navigate("/organisation")}
								className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
								aria-label="Zu Mein Labor"
								data-testid="einrichtung-go-to-organisation"
							>
								Zu "Mein Labor"
							</button>
							<button
								onClick={() => navigate("/onboarding")}
								className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
								aria-label="Einrichtungshilfe öffnen"
								data-testid="einrichtung-open-setup"
							>
								Einrichtungshilfe öffnen
							</button>
						</div>
					)}
				</div>
			</Collapsible.Content>
		</Collapsible.Root>
	);
};
